import { Injectable, inject } from "@angular/core"
import { NonRosterUser, RosterParticipant, AccountDetails, AccountDetailsApi } from "../models"
import { Observable } from "rxjs"
import { map, tap } from "rxjs/operators"
import { TripService } from "./trip.service"
import { TripFinanceService } from "./trip-finance.service"
import { EnvService } from "./env.service"
import { HttpClient } from "@angular/common/http"
import { BackgroundCheckTypes } from "../models"
import { SpecialtySelection } from "../models/forms-models"
import { MyInfoService } from "./my-info.service"
import { MinistrySurveyPatch } from "./ministry-survey/ministry-survey-fields"

export interface QuickAddParticipant {
    firstname: string;
    lastname: string;
    email: string;
}

export interface PastParticipant {
    id: number;
    firstname: string;
    lastname: string;
    email: string;
}

export interface AddNonRosterUserParticipant extends QuickAddParticipant {
    rolelead?: number;
    rolefin?: number;
    roledrive?: number;
}

/** A leader's Edit Info save: dirty account fields, specialties, and Ministry Survey answers. */
export type ParticipantInfoPatch = Partial<Omit<AccountDetails, 'specialties'>> & {
    specialties?: SpecialtySelection[];
    ministrySurvey?: MinistrySurveyPatch;
};

export interface AddParticipantsResponse {
    rosterSizeModified: boolean;
    rosterSize: number;
}

type PermissionsBody = {
    tripId: number,
    userId: number,
    rolelead: number,
    rolefin: number,
    roledrive: number
  }

type RemoveParticipantsBody = {
  tripId: number;
  userIds: number[];
};

type ResetPasswordBody = {
  tripId: number;
  userId: number;
};

type ResendInvitationBody = {
  tripId: number;
  userIds: number[];
};

type ParticipantMessageResponse = {
  success: boolean;
  failures?: string[];
};

export interface UpdateParticipantBackgroundCheckBody {
  tripId: number;
  userId: number;
  backgroundCheckDate: string | null;
  backgroundCheckType: BackgroundCheckTypes;
  backgroundCheckProvider?: number | null;
}

@Injectable({ providedIn: 'root' })
export class GroupManagementService {

    tripService = inject(TripService);
    tripFinanceService = inject(TripFinanceService);
    envService = inject(EnvService);
    http = inject(HttpClient);
    private readonly myInfoService = inject(MyInfoService);

    addParticipant(
        tripId: number,
        participant: QuickAddParticipant,
    ): Observable<AddParticipantsResponse> {
        return this.addParticipants(tripId, [participant]);
    }

    addParticipants(
        tripId: number,
        participants: QuickAddParticipant[],
    ): Observable<AddParticipantsResponse> {
        return this.http
            .post<AddParticipantsResponse>(`${this.envService.apiUrl}/participants/`, {
                tripId,
                participants,
            })
            .pipe(
                tap((response) => {
                    const trip = this.tripService.currentTrip.value();

                    if (trip && trip.id === tripId && response.rosterSizeModified) {
                        this.tripService.currentTrip.set({
                            ...trip,
                            attendees: response.rosterSize,
                        });
                    }

                    this.tripService.currentTrip.reload();
                    this.tripFinanceService.notifyFinanceChanged();
                }),
            );
    }

    addNonRosterUser(
        tripId: number,
        participant: AddNonRosterUserParticipant,
    ): Observable<AddParticipantsResponse> {
        return this.http
            .post<AddParticipantsResponse>(`${this.envService.apiUrl}/participants/`, {
                tripId,
                participants: [
                    {
                        ...participant,
                        permissionsOnly: true,
                    },
                ],
            })
            .pipe(tap(() => this.tripService.currentTrip.reload()));
    }

    assignParent(
        tripId: number,
        childUserId: number,
        parentUserId: number | null,
    ): Observable<void> {
        return this.http.put<void>(`${this.envService.apiUrl}/participants/parent`, {
            tripId,
            childUserId,
            parentUserId: parentUserId ?? 0,
        });
    }

    resetPassword(tripId: number, userId: number): Observable<void> {
        return this.http.post<void>(`${this.envService.apiUrl}/participants/reset-password`, {
            tripId,
            userId,
        } satisfies ResetPasswordBody);
    }

    resendInvitation(tripId: number, userIds: number[]): Observable<void> {
        return this.http.post<void>(`${this.envService.apiUrl}/participants/resend-invitation`, {
            tripId,
            userIds,
        } satisfies ResendInvitationBody);
    }

    sendParticipantMessage(
        tripId: number,
        userId: number,
        messageBody: string,
    ): Observable<ParticipantMessageResponse> {
        return this.sendParticipantMessages(tripId, [userId], messageBody);
    }

    sendParticipantMessages(
        tripId: number,
        userIds: number[],
        messageBody: string,
    ): Observable<ParticipantMessageResponse> {
        return this.http.post<ParticipantMessageResponse>(
            `${this.envService.apiUrl}/trips/${tripId}/participants/message`,
            { messageBody, userIds },
        );
    }

    removeParticipants(tripId: number, userIds: number[]): Observable<void> {
        return this.http.post<void>(`${this.envService.apiUrl}/participants/remove`, {
            tripId,
            userIds,
        } satisfies RemoveParticipantsBody).pipe(
            tap(() => {
                this.tripService.currentTrip.reload();
                this.tripFinanceService.notifyFinanceChanged();
            }),
        );
    }

    updateParticipantBackgroundCheck(
        body: UpdateParticipantBackgroundCheckBody,
    ): Observable<void> {
        return this.http.put<void>(`${this.envService.apiUrl}/participants/background-check`, body).pipe(
            tap(() => this.tripService.currentTrip.reload()),
        );
    }

    /** One request; the server commits account fields, specialties, and survey answers atomically. */
    patchParticipantInfo(
        tripId: number,
        userId: number,
        data: ParticipantInfoPatch,
    ): Observable<void> {
        const { ministrySurvey, ...accountDetails } = data;
        const payload: Record<string, unknown> = this.myInfoService.buildPatchPayload(accountDetails);
        if (ministrySurvey && Object.keys(ministrySurvey).length > 0) {
            payload['ministrySurvey'] = ministrySurvey;
        }
        return this.http
            .patch<AccountDetailsApi>(
                `${this.envService.apiUrl}/trips/${tripId}/participants/${userId}/info`,
                payload,
            )
            .pipe(
                map(() => undefined),
                tap(() => this.tripService.currentTrip.reload()),
            );
    }

    updatePermissions(
        participant: RosterParticipant | NonRosterUser,
        permissions: { rolelead?: number; rolefin?: number; roledrive?: number },
    ): Observable<RosterParticipant | NonRosterUser> {
        const rolelead = permissions.rolelead ?? participant.newtripUser.rolelead ?? 0;
        const rolefin = permissions.rolefin ?? participant.newtripUser.rolefin ?? 0;
        const roledrive = permissions.roledrive ?? participant.newtripUser.roledrive ?? 0;

        const body: PermissionsBody = {
            tripId: participant.newtripUser.tripId,
            userId: participant.newtripUser.newuserId,
            rolelead,
            rolefin,
            roledrive,
        };

        return this.http.put<void>(`${this.envService.apiUrl}/participants/permissions/`, body).pipe(
            tap(() => this.tripService.currentTrip.reload()),
            map(() => ({
                ...participant,
                newtripUser: {
                    ...participant.newtripUser,
                    rolelead,
                    rolefin,
                    roledrive,
                },
            })),
        );
    }

    promoteGl(tripId: number, newGlId: number): Observable<void> {
        return this.http.post<void>(
            `${this.envService.apiUrl}/participants/trip/${tripId}/promote-gl`,
            { newGlId },
        );
    }

    getPastParticipants(tripId: number): Observable<PastParticipant[]> {
        return this.http.get<PastParticipant[]>(
            `${this.envService.apiUrl}/trips/${tripId}/past-participants`,
        );
    }
}
